import { useEffect, useMemo, useState } from 'react';
import type { AdminDryRunResponse, NormalizedTxnWithCustom, PatternBased, PreDetectRequest, RuleBased } from '@kkp-fraud/shared';
import { ApiError, api, loadSample, loadSampleIndex, sampleName, type ApiSettings, type SampleScenario } from '../api';
import { skippedPatternScopes } from '../branches';
import { txnFieldValue } from '../fields';
import { formatRuleCondition, ms } from '../format';
import { t } from '../i18n';
import { validationLine } from '../ruleCheck';
import { fill } from './Interpolated';
import type { RuleSetDraft } from '../hooks/useRuleSetDraft';
import { ActionChip, DecisionChip } from './Chips';
// Wave 7 long windows
import { HitDetail, LongWindowNotes } from './LongWindowNotes';
import { Tip } from './PageHead';
// 2c rule sets
import { RuleSetOutcomes, hitKey } from './RuleSetOutcomes';

/**
 * Tab 3 — "what would this rule do?". The dry run always posts the **draft** as
 * `ruleSet`, so an analyst is testing the change on screen, not the document the engine is
 * already running. The API writes nothing: no velocity event, no audit record.
 */

/** The API returns hits and the normalized transaction; skipped/unfired is derived here. */
function analyseCoverage(ruleSet: RuleSetDraft['draft'], txn: NormalizedTxnWithCustom | undefined, firedIds: string[]) {
  const fired = new Set(firedIds);
  const skipped: { id: string; name: string; why: string }[] = [];
  const unfired: { id: string; name: string; condition: string }[] = [];
  const disabled: { id: string; name: string }[] = [];
  if (!ruleSet) return { skipped, unfired, disabled };

  const all: (RuleBased | PatternBased)[] = [...ruleSet.ruleBased, ...ruleSet.patternBased];
  for (const rule of all) {
    if (!rule.enabled) {
      disabled.push({ id: rule.id, name: rule.name });
      continue;
    }
    if (fired.has(rule.id)) continue;

    const missing: string[] = [];
    if (txn) {
      // `clauses` is the discriminant — `requires` is optional and absent on most RB rules.
      if ('clauses' in rule) {
        for (const f of rule.requires ?? []) {
          // Wave 6: a required custom field is read off `normalized.custom`.
          const v = txnFieldValue(txn, f);
          if (v === null || v === undefined) missing.push(String(f));
        }
      } else {
        missing.push(...skippedPatternScopes(rule, txn));
      }
    }
    if (missing.length > 0) skipped.push({ id: rule.id, name: rule.name, why: t('dryrun.skipped.why', { fields: missing.join(', ') }) });
    else unfired.push({ id: rule.id, name: rule.name, condition: formatRuleCondition(rule) });
  }
  return { skipped, unfired, disabled };
}

function NormalizedView({ txn }: { txn: NormalizedTxnWithCustom }) {
  // Wave 6: the custom values the rules read, one row each after the standard fields.
  const { custom, ...standard } = txn;
  const entries = [
    ...(Object.entries(standard) as [string, unknown][]),
    ...Object.entries(custom ?? {}).map(([k, v]) => [`custom.${k}`, v] as [string, unknown]),
  ];
  return (
    <details>
      <summary>{t('dryrun.normalized.summary', { n: entries.length })}</summary>
      <div className="kv" style={{ marginTop: 6 }}>
        {entries.map(([k, v]) => (
          <div className="kv-row" key={k} data-testid={k.startsWith('custom.') ? `normalized-${k.replace('.', '-')}` : undefined}>
            <span className="k">{k}</span>
            <span className={`v${v === null || v === undefined ? ' null' : ''}`}>
              {v === null || v === undefined ? 'null' : typeof v === 'string' ? v : JSON.stringify(v)}
            </span>
          </div>
        ))}
      </div>
    </details>
  );
}

export function DryRunTab({ state, settings }: { state: RuleSetDraft; settings: ApiSettings }) {
  const [scenarios, setScenarios] = useState<SampleScenario[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [index, setIndex] = useState(0);
  const [payload, setPayload] = useState<string>('');
  const [result, setResult] = useState<AdminDryRunResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [useDraft, setUseDraft] = useState(true);

  useEffect(() => {
    loadSampleIndex()
      .then(setScenarios)
      .catch(() => setScenarios([]));
  }, []);

  const pick = async (file: string, which = 0) => {
    setSelected(file);
    setIndex(which);
    setError(null);
    try {
      const requests = await loadSample(file);
      const req = requests[Math.min(which, requests.length - 1)];
      setPayload(JSON.stringify(req, null, 2));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const run = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const req = JSON.parse(payload) as PreDetectRequest;
      const res = await api.dryRun(settings, req, useDraft && state.draft ? state.draft : undefined);
      setResult(res);
    } catch (e) {
      if (e instanceof ApiError && e.validation) {
        setError(`${e.message}: ${validationLine(e.validation)}`);
      } else if (e instanceof SyntaxError) {
        setError(t('dryrun.invalidJson.error', { message: e.message }));
      } else {
        setError((e as Error).message);
      }
    } finally {
      setRunning(false);
    }
  };

  const coverage = useMemo(
    () => analyseCoverage(state.draft, result?.normalized, result?.reasons ?? []),
    [state.draft, result],
  );

  const scenario = scenarios.find((s) => s.file === selected);

  return (
    <div>
      <div>
        <section className="card">
          <header className="card-head">
            <h2>{t('dryrun.payload.title')}</h2>
            {/* What the button actually does, on the panel that holds it — a section whose
                whole body was an `i` was a heading with nothing under it. */}
            <Tip label={t('dryrun.about.title')} align="right">
              <p style={{ margin: 0 }}>{t('dryrun.about.p1')}</p>
              <p style={{ margin: '6px 0 0' }}>
                {fill(t('dryrun.about.p2'), { '{toggle}': <em>{t('dryrun.useDraft.label')}</em> })}
              </p>
              <p style={{ margin: '6px 0 0' }}>{t('dryrun.about.p3')}</p>
            </Tip>
            <span className="spacer" />
            <label className="check small">
              <input type="checkbox" checked={useDraft} onChange={(e) => setUseDraft(e.target.checked)} aria-label={t('dryrun.useDraft.a11y')} />
              {t('dryrun.useDraft.label')} {state.dirty ? t('dryrun.useDraft.dirty') : t('dryrun.useDraft.clean')}
            </label>
            <button type="button" className="btn primary" onClick={run} disabled={running || !payload.trim()}>
              {running ? t('dryrun.running.label') : t('dryrun.run.action')}
            </button>
          </header>

          <div className="card-body stack">
            <div className="row-wrap">
              <span className="small muted">{t('dryrun.samples.label')}</span>
              {scenarios.length === 0 && <span className="small muted">{t('dryrun.samples.empty', { type: 'PreDetectRequest' })}</span>}
              {scenarios.map((s) => (
                <button
                  key={s.file}
                  type="button"
                  className={`btn small${selected === s.file ? ' primary' : ''}`}
                  onClick={() => void pick(s.file, 0)}
                  title={s.expected}
                >
                  {sampleName(s)}
                  {s.requests > 1 ? ` (${s.requests})` : ''}
                </button>
              ))}
            </div>

            {scenario && (
              <div className="banner small">
                <strong>{sampleName(scenario)}</strong> {t('dryrun.expected.label')} {scenario.expected || t('dryrun.expected.none')}
                {scenario.requests > 1 && (
                  <div className="row-wrap" style={{ marginTop: 6 }}>
                    <span className="muted">{t('dryrun.request.label')}</span>
                    {scenario.transactionIds.map((id, i) => (
                      <button key={id} type="button" className={`btn small${index === i ? ' primary' : ''}`} onClick={() => void pick(scenario.file, i)}>
                        {i + 1}. {id}
                      </button>
                    ))}
                    <span className="muted small">
                      {t('dryrun.velocity.note')}
                    </span>
                  </div>
                )}
              </div>
            )}

            <label className="field">
              PreDetectRequest JSON
              <textarea
                aria-label="PreDetectRequest JSON"
                className="mono"
                style={{ minHeight: 220 }}
                value={payload}
                placeholder={t('dryrun.request.placeholder')}
                onChange={(e) => setPayload(e.target.value)}
              />
            </label>

            {error && (
              <div className="banner error" role="alert">
                {error}
              </div>
            )}
          </div>
        </section>

        {result && (
          <>
            <section className="card">
              <header className="card-head">
                <h2>{t('dryrun.result.title')}</h2>
                <span className="spacer" />
                <span className="small muted mono">{t('dryrun.ruleset.label', { version: result.ruleSetVersion })}</span>
              </header>
              <div className="card-body stack">
                <div className="row-wrap">
                  <DecisionChip decision={result.decision} big />
                  <div className="tiles" style={{ flex: 1 }}>
                    <div className="tile">
                      <div className="label">{t('dryrun.tile.responseCode')}</div>
                      <div className="value">{result.response_code_hint}</div>
                    </div>
                    <div className="tile">
                      <div className="label">{t('dryrun.tile.stepUpMethod')}</div>
                      <div className="value" style={{ fontSize: 13 }}>
                        {result.step_up_method}
                      </div>
                    </div>
                    <div className="tile">
                      <div className="label">{t('dryrun.tile.latency')}</div>
                      <div className="value" style={{ fontSize: 15 }}>
                        {ms(result.engine_latency_ms)}
                      </div>
                    </div>
                  </div>
                </div>
                {result.alert && <div className="banner small">{t('dryrun.alert.note', { action: 'ALERT_ONLY' })}</div>}
                {/* Wave 7 — where each long window read here starts counting */}
                <LongWindowNotes notes={result.longWindows} />
              </div>
            </section>

            <section className="card">
              <header className="card-head">
                <h2>{t('dryrun.hits.title', { n: result.hits.length })}</h2>
              </header>
              <div className="tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 70 }}>{t('col.rule.label')}</th>
                      <th style={{ width: 128 }}>{t('col.action.label')}</th>
                      <th>{t('col.name.label')}</th>
                      <th>{t('col.detail.label')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.hits.length === 0 && (
                      <tr>
                        <td colSpan={4} className="empty">
                          {t('dryrun.hits.empty')}
                        </td>
                      </tr>
                    )}
                    {result.hits.map((h) => (
                      // 2c rule sets: two sets may both fire an RB-01 — the set tells them apart
                      <tr key={hitKey(h)} data-testid={`hit-${hitKey(h)}`}>
                        <td className="mono nowrap">
                          {h.rule_id}
                          {h.rule_set_id && <div className="small muted">{h.rule_set_id}</div>}
                        </td>
                        <td>
                          <ActionChip action={h.action} />
                        </td>
                        <td>{h.name}</td>
                        <td className="mono small">
                          <HitDetail detail={h.detail} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* 2c rule sets: each set's own outcome, when two or more were evaluated */}
            <RuleSetOutcomes outcomes={result.rule_sets} />

            <section className="card">
              <header className="card-head">
                <h2>{t('dryrun.coverage.title')}</h2>
                <span className="chip warn" data-testid="skipped-count">{t('dryrun.skipped.chip', { n: coverage.skipped.length })}</span>
                <span className="chip" data-testid="unfired-count">{t('dryrun.unfired.chip', { n: coverage.unfired.length })}</span>
                <span className="chip" data-testid="disabled-count">{t('rules.disabled.chip', { n: coverage.disabled.length })}</span>
              </header>
              <div className="card-body stack">
                {coverage.skipped.length > 0 && (
                  <div className="banner warn small">
                    <strong>{t('dryrun.skipped.lead')}</strong> {t('dryrun.skipped.note')}
                    <ul>
                      {coverage.skipped.map((s) => (
                        <li key={s.id}>
                          <span className="mono">{s.id}</span> {s.name} — {s.why}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <details>
                  <summary>{t('dryrun.unfired.summary', { n: coverage.unfired.length })}</summary>
                  <table style={{ marginTop: 6 }}>
                    <tbody>
                      {coverage.unfired.map((u) => (
                        <tr key={u.id}>
                          <td className="mono nowrap" style={{ width: 70 }}>
                            {u.id}
                          </td>
                          <td style={{ width: 240 }}>{u.name}</td>
                          <td className="mono small">{u.condition}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
                {coverage.disabled.length > 0 && (
                  <details>
                    <summary>{t('dryrun.disabled.summary', { n: coverage.disabled.length })}</summary>
                    <div className="small mono" style={{ marginTop: 4 }}>
                      {coverage.disabled.map((d) => `${d.id} ${d.name}`).join(' · ')}
                    </div>
                  </details>
                )}
              </div>
            </section>

            <section className="card">
              <div className="card-body">
                <NormalizedView txn={result.normalized} />
              </div>
            </section>
          </>
        )}
      </div>

    </div>
  );
}
